import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Dropdown, Form, Icon, Input, Message, Modal, Popup } from 'semantic-ui-react';
import styled from 'styled-components';

import { StyledAddButton, StyledButton, StyledIconButton, StyledSaveButton } from 'common/components/button/StyledButton';
import { Card } from 'common/components/Card';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { StyledDateTimeInput } from 'common/components/DateTimeInput';
import LoadingSpinner from 'common/components/LoadingSpinner';
import CronExpressionInput, { validateCronExpression } from 'common/components/CronExpressionInput';
import PositionedModal from 'common/components/PositionedModal';
import { PILLARS } from 'common/constants/pillars';
import { H2, PageContent, PageHeader, PageSection, Subtitle } from 'common/styles/page';
import { FieldLabel, FormRow } from 'common/styles/page';
import {
  getCurrentOrganization,
  getCurrentOrganizationTimezone,
  isCurrentUserStaffOrSuperAdmin,
} from 'common/selectors/auth';
import { getImpersonationMode, ImpersonationMode } from 'common/slices/impersonationSlice';
import { useSelector } from 'react-redux';
import { useToasts } from 'common/toast';
import { isEmployeeStatusActive } from 'areas/settings/employee-management/utils/EmployeeUtils';
import { useGetAllTeamsQuery, useGetOrganizationMembersQuery } from 'areas/team-management/api/teamApi';

import {
  AssessmentQuestionSetOption,
  AssessmentQuestionSetQuestion,
  PulseSurveyStructure,
  PulseSurveyStructureWritePayload,
  PulseSurveyWritePayload,
  useCreatePulseQuestionSetMutation,
  useCreatePulseSurveyMutation,
  useInvitePulseParticipantsMutation,
  useListPulseQuestionSetsQuery,
  usePatchPulseQuestionSetMutation,
  useListPulseSurveyStructuresQuery,
  useUpdatePulseSurveyStructureMutation,
} from '../api/pulseSurveyPortalApi';

type PulseScheduleStructureInviteAudience =
  | { type: 'organization' }
  | { type: 'team' };

type PulseScheduleStructurePayload = PulseSurveyWritePayload & PulseSurveyStructureWritePayload;

const RECURS_ONETIME = 1;
const RECURS_RRULE = 2;
const DEFAULT_DURATION_DAYS = 7;
const LIBRARY_QUESTION_SET_TITLE = 'nuLogic Library';
const QUESTION_SET_TYPE_PULSE = 1;
const QUESTION_SET_SCOPE_ORG = 2;
const MAX_PREVIEW_OCCURRENCES = 3;
const QUESTION_TYPE_LIKERT = 1;
const QUESTION_TYPE_FEEDBACK = 2;
const QUESTION_TYPE_MULTIPLE_CHOICE_SELECT = 3;
const QUESTION_TYPE_MULTIPLE_CHOICE_RADIO = 4;
const QUESTION_TYPE_MULTIPLE_CHOICE_CHECKBOX = 5;
const CREATE_DEFAULT_REMINDER_SCHEDULE = '0 9 */2 * *';
const TEAM_SELECTION_MODAL_HEIGHT = 'min(600px, 80vh)';

const QUESTION_TYPE_OPTIONS = [
  { key: QUESTION_TYPE_LIKERT, value: QUESTION_TYPE_LIKERT, text: 'Agreement scale' },
  { key: QUESTION_TYPE_FEEDBACK, value: QUESTION_TYPE_FEEDBACK, text: 'Feedback' },
  { key: QUESTION_TYPE_MULTIPLE_CHOICE_SELECT, value: QUESTION_TYPE_MULTIPLE_CHOICE_SELECT, text: 'Multiple choice (select menu)' },
  { key: QUESTION_TYPE_MULTIPLE_CHOICE_RADIO, value: QUESTION_TYPE_MULTIPLE_CHOICE_RADIO, text: 'Multiple choice (radio buttons)' },
  { key: QUESTION_TYPE_MULTIPLE_CHOICE_CHECKBOX, value: QUESTION_TYPE_MULTIPLE_CHOICE_CHECKBOX, text: 'Multiple choice (checkboxes)' },
];

type RecurrencePreset = 'one_time' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'custom';
type RepeatUntilMode = 'none' | 'date';

const RECURRENCE_PRESET_OPTIONS = [
  { key: 'one_time', value: 'one_time', text: 'One time' },
  { key: 'weekly', value: 'weekly', text: 'Weekly' },
  { key: 'biweekly', value: 'biweekly', text: 'Every 2 weeks' },
  { key: 'monthly', value: 'monthly', text: 'Monthly' },
  { key: 'quarterly', value: 'quarterly', text: 'Quarterly' },
  { key: 'custom', value: 'custom', text: 'Custom' },
];

const REPEAT_UNTIL_OPTIONS = [
  { key: 'none', value: 'none', text: 'No end date' },
  { key: 'date', value: 'date', text: 'Specific date' },
];

const THEME_OPTIONS = [
  { id: 1, label: 'Engagement & Belonging' },
  { id: 2, label: 'Communication & Voice' },
  { id: 3, label: 'Growth & Development' },
  { id: 4, label: 'Fairness/Recognition & Rewards' },
  { id: 5, label: 'Leadership & Manager Support' },
  { id: 6, label: 'Collaboration & Relationships' },
  { id: 7, label: 'Workload & Wellbeing' },
  { id: 8, label: 'Trust & Psychological Safety' },
  { id: 9, label: 'Diversity & Representation' },
] as const;

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const addMonths = (date: Date, months: number) => {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
};

const formatDateTime = (value: Date) =>
  value.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

const formatDateOnly = (value: Date) =>
  value.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const durationDaysBetween = (startDate: Date, endDate: Date) => {
  const msPerDay = 24 * 60 * 60 * 1000;
  const days = Math.floor((endDate.getTime() - startDate.getTime()) / msPerDay);
  return Math.max(1, days);
};

type FormState = {
  name: string;
  assessmentQuestionSet?: number;
  durationDays: number;
  startDateTime: Date;
  reminderEnabled: boolean;
  reminderSchedule: string;
  recurrencePreset: RecurrencePreset;
  recurrenceRule: string;
  repeatUntilMode: RepeatUntilMode;
  repeatUntilDate: Date | null;
  isActive: boolean;
};

type LibraryQuestion = Required<Pick<AssessmentQuestionSetQuestion, 'question'>> & {
  id: number;
  pillar?: number | null;
  type?: number;
  theme?: number | null;
  sort_order?: number;
  config?: Record<string, unknown>;
  allow_feedback?: boolean;
  required?: boolean;
};

type DisplayQuestion = Required<Pick<AssessmentQuestionSetQuestion, 'question'>> & {
  id?: number;
  pillar?: number | null;
  type?: number;
  theme?: number | null;
  sort_order?: number;
  config?: Record<string, unknown>;
  allow_feedback?: boolean;
  required?: boolean;
};

type QuestionSelectionMode = 'library' | 'custom';
type LibraryFilterMode = 'themes' | 'pillars';

type CustomQuestionDraft = {
  localId: string;
  question: string;
  type: number;
  options: string[];
  maxSelections?: number;
  existingQuestionId?: number;
};

const normalizeTeamIds = (ids?: number[]) =>
  Array.from(new Set((ids ?? []).filter((id) => Number.isFinite(id))));

const teamIdsFromStructure = (value: PulseSurveyStructure | null | undefined) =>
  normalizeTeamIds(value?.team_ids && value.team_ids.length > 0 ? value.team_ids : []);

const normalizeLibraryQuestions = (value?: AssessmentQuestionSetQuestion[]) =>
  (value ?? [])
    .filter((q): q is LibraryQuestion => typeof q.id === 'number' && typeof q.question === 'string')
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

const normalizeDisplayQuestions = (value?: AssessmentQuestionSetQuestion[]) =>
  (value ?? [])
    .filter((q): q is DisplayQuestion => typeof q.question === 'string')
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

const getThemeLabel = (themeId?: number | null) => {
  if (themeId === null || themeId === undefined) {
    return 'My Organization';
  }

  if (!themeId) {
    return 'Unassigned';
  }

  return THEME_OPTIONS.find((theme) => theme.id === themeId)?.label || 'Unassigned';
};

const getPillarLabel = (pillarId?: number | null) => {
  if (pillarId === null || pillarId === undefined) {
    return 'Unassigned';
  }

  return PILLARS.find((pillar) => pillar.id === pillarId)?.name || 'Unassigned';
};

const buildGeneratedQuestionSetTitle = (structureName?: string, orgName?: string) => {
  const safeStructureName = (structureName || '').trim();
  if (safeStructureName) {
    return safeStructureName;
  }

  const dateStamp = new Date().toISOString().slice(0, 10);
  const safeOrg = (orgName || 'Organization').trim();
  return `${safeOrg} Pulse Questions ${dateStamp}`;
};

const isFeedbackQuestionType = (type?: number | null) => (type ?? QUESTION_TYPE_LIKERT) === QUESTION_TYPE_FEEDBACK;

const isMultipleChoiceQuestionType = (type?: number | null) => {
  const resolved = type ?? QUESTION_TYPE_LIKERT;
  return (
    resolved === QUESTION_TYPE_MULTIPLE_CHOICE_SELECT
    || resolved === QUESTION_TYPE_MULTIPLE_CHOICE_RADIO
    || resolved === QUESTION_TYPE_MULTIPLE_CHOICE_CHECKBOX
  );
};

const isCheckboxQuestionType = (type?: number | null) =>
  (type ?? QUESTION_TYPE_LIKERT) === QUESTION_TYPE_MULTIPLE_CHOICE_CHECKBOX;

const getQuestionTypeLabel = (type?: number | null) => {
  const resolved = type ?? QUESTION_TYPE_LIKERT;
  if (resolved === QUESTION_TYPE_FEEDBACK) return 'Feedback';
  if (resolved === QUESTION_TYPE_MULTIPLE_CHOICE_SELECT) return 'Multiple choice (select menu)';
  if (resolved === QUESTION_TYPE_MULTIPLE_CHOICE_RADIO) return 'Multiple choice (radio buttons)';
  if (resolved === QUESTION_TYPE_MULTIPLE_CHOICE_CHECKBOX) return 'Multiple choice (checkboxes)';
  return 'Agreement scale';
};

const normalizeMultipleChoiceOptions = (options?: unknown): string[] => {
  if (!Array.isArray(options)) return [];
  return options
    .map((option) => String(option ?? '').trim())
    .filter((option) => option.length > 0);
};

const getQuestionConfigIdentity = (question: { config?: Record<string, unknown> }) => {
  const options = normalizeMultipleChoiceOptions(question.config?.options);
  const renderMode = question.config?.render_mode === 'radio'
    ? 'radio'
    : question.config?.render_mode === 'checkbox'
      ? 'checkbox'
      : question.config?.render_mode === 'select'
        ? 'select'
        : '';
  if (options.length === 0 && !renderMode) return '';
  return `${renderMode}|${options.join('|')}`;
};

const questionIdentityKey = (question: {
  question: string;
  type?: number | null;
  theme?: number | null;
  pillar?: number | null;
  config?: Record<string, unknown>;
}) => {
  const normalizedText = question.question.trim().toLowerCase();
  const type = question.type ?? QUESTION_TYPE_LIKERT;
  const theme = question.theme ?? null;
  const pillar = question.pillar ?? null;
  const configIdentity = getQuestionConfigIdentity(question);
  return `${normalizedText}::${type}::${theme ?? 'null'}::${pillar ?? 'null'}::${configIdentity}`;
};

const errorDetail = (err: any, fallback: string) => String(err?.data?.detail || err?.data?.error || fallback);

const untilAsRruleValue = (value: Date) => value.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

const stripUntilClause = (rule: string) =>
  rule
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part && !part.startsWith('UNTIL='))
    .join(';');

const parseUntilFromRule = (rule: string): Date | null => {
  const match = rule.match(/(?:^|;)UNTIL=([^;]+)/);
  if (!match?.[1]) return null;

  const normalized = match[1].replace('Z', '');
  const parsed =
    normalized.length >= 15
      ? new Date(
          Date.UTC(
            Number(normalized.slice(0, 4)),
            Number(normalized.slice(4, 6)) - 1,
            Number(normalized.slice(6, 8)),
            Number(normalized.slice(9, 11)),
            Number(normalized.slice(11, 13)),
            Number(normalized.slice(13, 15))
          )
        )
      : new Date(match[1]);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const inferRecurrencePreset = (recurrenceType: number, recurrenceRule?: string | null): RecurrencePreset => {
  if (recurrenceType !== RECURS_RRULE || !recurrenceRule?.trim()) return 'one_time';

  const baseRule = stripUntilClause(recurrenceRule.toUpperCase());
  if (baseRule.includes('FREQ=WEEKLY') && baseRule.includes('INTERVAL=2')) return 'biweekly';
  if (baseRule.includes('FREQ=WEEKLY')) return 'weekly';
  if (baseRule.includes('FREQ=MONTHLY') && baseRule.includes('INTERVAL=3')) return 'quarterly';
  if (baseRule.includes('FREQ=MONTHLY')) return 'monthly';
  return 'custom';
};

const recurringBaseRuleForPreset = (preset: RecurrencePreset) => {
  switch (preset) {
    case 'weekly':
      return 'FREQ=WEEKLY;INTERVAL=1';
    case 'biweekly':
      return 'FREQ=WEEKLY;INTERVAL=2';
    case 'monthly':
      return 'FREQ=MONTHLY;INTERVAL=1';
    case 'quarterly':
      return 'FREQ=MONTHLY;INTERVAL=3';
    default:
      return '';
  }
};

const nextStartForPreset = (start: Date, preset: RecurrencePreset) => {
  switch (preset) {
    case 'weekly':
      return addDays(start, 7);
    case 'biweekly':
      return addDays(start, 14);
    case 'monthly':
      return addMonths(start, 1);
    case 'quarterly':
      return addMonths(start, 3);
    default:
      return start;
  }
};

const createCustomQuestionDraft = (seed: number): CustomQuestionDraft => ({
  localId: `custom-${Date.now()}-${seed}`,
  question: '',
  type: QUESTION_TYPE_LIKERT,
  options: ['', ''],
  maxSelections: 3,
});

const buildInitialFormState = (
  structure: PulseSurveyStructure | null,
  questionSetId: number | undefined,
  defaultStartDate: Date,
  defaultReminderSchedule: string
): FormState => {
  if (structure) {
    const durationDays = structure.duration_days || durationDaysBetween(new Date(structure.first_start_date_time), new Date(structure.first_end_date_time));
    const recurrencePreset = inferRecurrencePreset(structure.recurrence_type || RECURS_ONETIME, structure.recurrence_rule);
    const repeatUntilDate = recurrencePreset === 'one_time' ? null : parseUntilFromRule(structure.recurrence_rule || '');
    const repeatUntilMode: RepeatUntilMode = repeatUntilDate ? 'date' : 'none';
    return {
      name: structure.name,
      assessmentQuestionSet: structure.assessment_question_set,
      durationDays,
      startDateTime: new Date(structure.first_start_date_time),
      reminderEnabled: Boolean(structure.reminder_enabled),
      reminderSchedule: structure.reminder_schedule || defaultReminderSchedule,
      recurrencePreset,
      recurrenceRule: recurrencePreset === 'custom' ? stripUntilClause(structure.recurrence_rule || '') : '',
      repeatUntilMode,
      repeatUntilDate,
      isActive: Boolean(structure.is_active),
    };
  }

  return {
    name: '',
    assessmentQuestionSet: questionSetId,
    durationDays: DEFAULT_DURATION_DAYS,
    startDateTime: defaultStartDate,
    reminderEnabled: true,
    reminderSchedule: defaultReminderSchedule,
    recurrencePreset: 'one_time',
    recurrenceRule: '',
    repeatUntilMode: 'none',
    repeatUntilDate: null,
    isActive: true,
  };
};

const PulseScheduleStructureEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { structureId } = useParams<{ structureId: string }>();
  const org = useSelector(getCurrentOrganization) as any;
  const orgTimezone = useSelector(getCurrentOrganizationTimezone) as string | undefined;
  const isStaffOrSuperAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const impersonationMode = useSelector(getImpersonationMode);

  const isEditMode = Boolean(structureId);
  const parsedStructureId = structureId ? Number(structureId) : null;

  const defaultReminderSchedule = isEditMode
    ? (org?.defaultReminderSchedule ?? org?.default_reminder_schedule ?? CREATE_DEFAULT_REMINDER_SCHEDULE)
    : CREATE_DEFAULT_REMINDER_SCHEDULE;
  const isOrgContextImpersonating = Boolean(org?.is_impersonating || org?.isImpersonating);
  const isImpersonating = impersonationMode === ImpersonationMode.ACTIVE || isOrgContextImpersonating;
  const showAdvancedReminderModes = isStaffOrSuperAdmin && !isImpersonating;

  const defaultStartDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d;
  }, []);

  const { data: structures = [], isLoading: isLoadingStructures, isFetching: isFetchingStructures, refetch } = useListPulseSurveyStructuresQuery();
  const { data: questionSets = [] } = useListPulseQuestionSetsQuery();
  const { data: teamsData } = useGetAllTeamsQuery(undefined, { skip: !org?.id });
  const { data: organizationMembersData } = useGetOrganizationMembersQuery(undefined, { skip: !org?.id });

  const [createPulseSurvey, { isLoading: isCreating }] = useCreatePulseSurveyMutation();
  const [updatePulseSurveyStructure, { isLoading: isUpdatingStructure }] = useUpdatePulseSurveyStructureMutation();
  const [createPulseQuestionSet, { isLoading: isCreatingQuestionSet }] = useCreatePulseQuestionSetMutation();
  const [patchPulseQuestionSet, { isLoading: isUpdatingQuestionSet }] = usePatchPulseQuestionSetMutation();
  const [invitePulseParticipants] = useInvitePulseParticipantsMutation();

  const structure = useMemo(
    () => (isEditMode && parsedStructureId ? structures.find((item) => item.id === parsedStructureId) ?? null : null),
    [isEditMode, parsedStructureId, structures]
  );

  const teams = useMemo(
    () =>
      (teamsData?.results ?? [])
        .map((team) => ({ id: team.id, name: team.name }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' })),
    [teamsData]
  );

  const activeOrganizationMemberIds = useMemo(
    () =>
      (organizationMembersData?.results ?? [])
        .filter((member) => isEmployeeStatusActive(member.status))
        .map((member) => member.id),
    [organizationMembersData]
  );

  const initialAssessmentQuestionSet = isEditMode ? (structure?.assessment_question_set ?? undefined) : undefined;

  const [form, setForm] = useState<FormState>(() =>
    buildInitialFormState(null, initialAssessmentQuestionSet, defaultStartDate, defaultReminderSchedule)
  );
  const [showReminderEditor, setShowReminderEditor] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  const [modalSelectedTeamIds, setModalSelectedTeamIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [libraryFilterMode, setLibraryFilterMode] = useState<LibraryFilterMode>('themes');
  const [activeThemeId, setActiveThemeId] = useState<number>(THEME_OPTIONS[0].id);
  const [activePillarId, setActivePillarId] = useState<number>(PILLARS[0]?.id ?? 0);
  const [questionSelectionMode, setQuestionSelectionMode] = useState<QuestionSelectionMode>('library');
  const [selectedLibraryQuestionIds, setSelectedLibraryQuestionIds] = useState<number[]>([]);
  const [customQuestions, setCustomQuestions] = useState<CustomQuestionDraft[]>([]);
  const [selectionInitialized, setSelectionInitialized] = useState(false);
  const [contextQuestionSetId, setContextQuestionSetId] = useState<number | null>(null);
  const [addedQuestionsPreview, setAddedQuestionsPreview] = useState<AssessmentQuestionSetQuestion[]>([]);
  const [questionSettingsDirty, setQuestionSettingsDirty] = useState(false);
  const [draggedQuestionIndex, setDraggedQuestionIndex] = useState<number | null>(null);
  const [isSaveLocked, setIsSaveLocked] = useState(false);

  useEffect(() => {
    if (isEditMode && isLoadingStructures) {
      return;
    }

    setForm(
      buildInitialFormState(
        isEditMode ? structure : null,
        initialAssessmentQuestionSet,
        defaultStartDate,
        defaultReminderSchedule
      )
    );
    const initialTeamIds = isEditMode ? teamIdsFromStructure(structure) : [];
    setSelectedTeamIds(initialTeamIds);
    setModalSelectedTeamIds(initialTeamIds);
    setTeamSearch('');
    setIsTeamModalOpen(false);
    setShowReminderEditor(false);
    setError(null);
    setIsQuestionModalOpen(false);
    setLibraryFilterMode('themes');
    setActiveThemeId(THEME_OPTIONS[0].id);
    setActivePillarId(PILLARS[0]?.id ?? 0);
    setQuestionSelectionMode('library');
    setSelectedLibraryQuestionIds([]);
    setCustomQuestions([]);
    setSelectionInitialized(false);
    setContextQuestionSetId(isEditMode ? (structure?.assessment_question_set ?? null) : null);
    setAddedQuestionsPreview([]);
    setQuestionSettingsDirty(false);
  }, [
    isEditMode,
    structure,
    initialAssessmentQuestionSet,
    defaultStartDate,
    defaultReminderSchedule,
    isLoadingStructures,
  ]);

  const libraryQuestionSet = useMemo(
    () =>
      questionSets.find(
        (set) =>
          set.title === LIBRARY_QUESTION_SET_TITLE &&
          set.scope === 3 &&
          set.type === QUESTION_SET_TYPE_PULSE
      ) || null,
    [questionSets]
  );

  const contextQuestionSet = useMemo(
    () => questionSets.find((set) => set.id === form.assessmentQuestionSet) || null,
    [questionSets, form.assessmentQuestionSet]
  );

  const libraryQuestionsByTheme = useMemo(() => {
    const grouped: Record<number, LibraryQuestion[]> = {};
    THEME_OPTIONS.forEach((theme) => {
      grouped[theme.id] = [];
    });

    normalizeLibraryQuestions(libraryQuestionSet?.questions).forEach((question) => {
      if (question.theme !== null && question.theme !== undefined && grouped[question.theme]) {
        grouped[question.theme].push(question);
      }
    });

    return grouped;
  }, [libraryQuestionSet]);

  const libraryQuestionsByPillar = useMemo(() => {
    const grouped: Record<number, LibraryQuestion[]> = {};
    PILLARS.forEach((pillar) => {
      grouped[pillar.id] = [];
    });

    normalizeLibraryQuestions(libraryQuestionSet?.questions).forEach((question) => {
      if (question.pillar !== null && question.pillar !== undefined && grouped[question.pillar]) {
        grouped[question.pillar].push(question);
      }
    });

    return grouped;
  }, [libraryQuestionSet]);

  const selectedQuestionCountByTheme = useMemo(() => {
    const counts: Record<number, number> = {};

    THEME_OPTIONS.forEach((theme) => {
      counts[theme.id] = 0;
    });

    THEME_OPTIONS.forEach((theme) => {
      counts[theme.id] = (libraryQuestionsByTheme[theme.id] || []).reduce(
        (sum, question) => (selectedLibraryQuestionIds.includes(question.id) ? sum + 1 : sum),
        0
      );
    });

    return counts;
  }, [libraryQuestionsByTheme, selectedLibraryQuestionIds]);

  const selectedQuestionCountByPillar = useMemo(() => {
    const counts: Record<number, number> = {};

    PILLARS.forEach((pillar) => {
      counts[pillar.id] = 0;
    });

    PILLARS.forEach((pillar) => {
      counts[pillar.id] = (libraryQuestionsByPillar[pillar.id] || []).reduce(
        (sum, question) => (selectedLibraryQuestionIds.includes(question.id) ? sum + 1 : sum),
        0
      );
    });

    return counts;
  }, [libraryQuestionsByPillar, selectedLibraryQuestionIds]);

  const selectedCustomQuestionCount = useMemo(
    () => customQuestions.filter((question) => question.question.trim().length > 0).length,
    [customQuestions]
  );

  const totalSelectedQuestionCount = useMemo(
    () => selectedLibraryQuestionIds.length + selectedCustomQuestionCount,
    [selectedLibraryQuestionIds.length, selectedCustomQuestionCount]
  );

  const activeLibraryQuestions = useMemo(() => {
    if (libraryFilterMode === 'pillars') {
      return libraryQuestionsByPillar[activePillarId] || [];
    }
    return libraryQuestionsByTheme[activeThemeId] || [];
  }, [libraryFilterMode, libraryQuestionsByPillar, activePillarId, libraryQuestionsByTheme, activeThemeId]);

  const addedQuestionsForTable = useMemo(
    () => normalizeDisplayQuestions(addedQuestionsPreview),
    [addedQuestionsPreview]
  );

  useEffect(() => {
    if (!selectionInitialized && contextQuestionSet?.questions && libraryQuestionSet?.questions) {
      const libraryIdByQuestionText = new Map(
        normalizeLibraryQuestions(libraryQuestionSet.questions).map((question) => [question.question, question.id])
      );

      const matchedIds = normalizeLibraryQuestions(contextQuestionSet.questions)
        .map((question) => libraryIdByQuestionText.get(question.question))
        .filter((id): id is number => typeof id === 'number');

      const customFromContext = normalizeDisplayQuestions(contextQuestionSet.questions)
        .filter((question) => !libraryIdByQuestionText.has(question.question))
        .map((question, index) => {
          const resolvedType = question.type ?? QUESTION_TYPE_LIKERT;
          const existingOptions = normalizeMultipleChoiceOptions(question.config?.options);

          return {
            localId: `existing-custom-${index}`,
            question: question.question,
            type: resolvedType,
            options: isMultipleChoiceQuestionType(resolvedType)
              ? (existingOptions.length > 0 ? existingOptions : ['', ''])
              : existingOptions,
            maxSelections:
              isCheckboxQuestionType(resolvedType) && typeof question.config?.max_selections === 'number'
                ? question.config.max_selections
                : 3,
            existingQuestionId: question.id,
          };
        });

      setSelectedLibraryQuestionIds(matchedIds);
      setCustomQuestions(customFromContext);
      setContextQuestionSetId(contextQuestionSet.id);
      setAddedQuestionsPreview(contextQuestionSet.questions || []);
      setQuestionSettingsDirty(false);
      setSelectionInitialized(true);
    }
  }, [selectionInitialized, contextQuestionSet, libraryQuestionSet]);

  const setQuestionFlag = (rowIndex: number, field: 'required' | 'allow_feedback', checked: boolean) => {
    setAddedQuestionsPreview((prev) =>
      normalizeDisplayQuestions(prev).map((question, index) =>
        index === rowIndex ? { ...question, [field]: checked } : question
      )
    );
    setQuestionSettingsDirty(true);
  };

  const moveQuestionRow = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }

    setAddedQuestionsPreview((prev) => {
      const ordered = normalizeDisplayQuestions(prev);
      if (fromIndex < 0 || fromIndex >= ordered.length || toIndex < 0 || toIndex >= ordered.length) {
        return ordered;
      }

      const next = [...ordered];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);

      return next.map((question, index) => ({
        ...question,
        sort_order: index + 1,
      }));
    });

    setQuestionSettingsDirty(true);
  };

  const normalizedSelectedTeamIds = useMemo(
    () => normalizeTeamIds(selectedTeamIds),
    [selectedTeamIds]
  );
  const normalizedModalSelectedTeamIds = useMemo(
    () => normalizeTeamIds(modalSelectedTeamIds),
    [modalSelectedTeamIds]
  );
  const selectedTeamIdSet = useMemo(() => new Set(normalizedSelectedTeamIds), [normalizedSelectedTeamIds]);
  const modalSelectedTeamIdSet = useMemo(
    () => new Set(normalizedModalSelectedTeamIds),
    [normalizedModalSelectedTeamIds]
  );
  const filteredTeams = useMemo(() => {
    const query = teamSearch.trim().toLowerCase();
    if (!query) {
      return teams;
    }
    return teams.filter((team) => (team.name || '').toLowerCase().includes(query));
  }, [teams, teamSearch]);
  const selectedTeamsSummary = useMemo(() => {
    if (normalizedSelectedTeamIds.length === 0) {
      return {
        text: 'No teams selected',
        overflow: 0,
        title: 'No teams selected',
      };
    }

    const allSelectedNames = teams
      .filter((team) => selectedTeamIdSet.has(team.id))
      .map((team) => team.name)
      .filter((name): name is string => Boolean(name && name.trim()));
    const fullListTitle = allSelectedNames.join('\n');

    if (normalizedSelectedTeamIds.length === teams.length && teams.length > 0) {
      const allText = `All teams (${teams.length})`;
      return {
        text: allText,
        overflow: 0,
        title: fullListTitle || allText,
      };
    }

    const selectedNames = teams
      .filter((team) => selectedTeamIdSet.has(team.id))
      .map((team) => team.name)
      .slice(0, 3);
    const overflow = normalizedSelectedTeamIds.length - selectedNames.length;
    const text = selectedNames.join(', ');
    const title = fullListTitle || (overflow > 0 ? `${text} +${overflow} more` : text);

    return {
      text,
      overflow,
      title,
    };
  }, [normalizedSelectedTeamIds, selectedTeamIdSet, teams]);

  const toggleTeamSelection = (teamId: number) => {
    setModalSelectedTeamIds((prev) => {
      const exists = prev.includes(teamId);
      if (exists) {
        return prev.filter((id) => id !== teamId);
      }
      return [...prev, teamId];
    });
  };

  const selectAllTeams = () => {
    setModalSelectedTeamIds(teams.map((team) => team.id));
  };

  const clearModalTeamSelection = () => {
    setModalSelectedTeamIds([]);
  };

  const openTeamModal = async () => {
    let nextTeamIds = normalizedSelectedTeamIds;

    if (isEditMode && parsedStructureId) {
      try {
        const latestResult = await refetch();
        const latestStructures = (latestResult as { data?: PulseSurveyStructure[] }).data;
        const latestStructure =
          latestStructures?.find((item) => item.id === parsedStructureId) ?? structure;
        nextTeamIds = teamIdsFromStructure(latestStructure);
      } catch {
        nextTeamIds = teamIdsFromStructure(structure);
      }
    }

    setModalSelectedTeamIds(nextTeamIds);
    setTeamSearch('');
    setIsTeamModalOpen(true);
  };

  const closeTeamModal = () => {
    setIsTeamModalOpen(false);
    setTeamSearch('');
  };

  const applyTeamModalSelection = () => {
    setSelectedTeamIds(normalizedModalSelectedTeamIds);
    closeTeamModal();
  };

  const cronInfo = validateCronExpression(form.reminderSchedule || '', orgTimezone);
  const closesAt = useMemo(() => addDays(form.startDateTime, form.durationDays), [form.startDateTime, form.durationDays]);
  const effectiveRepeatUntilDate =
    form.repeatUntilMode === 'date' ? (form.repeatUntilDate ?? closesAt) : null;
  const isRepeatUntilDateValid =
    form.recurrencePreset === 'one_time' ||
    form.repeatUntilMode !== 'date' ||
    (effectiveRepeatUntilDate !== null && effectiveRepeatUntilDate > form.startDateTime);
  const recurrencePreviewData = useMemo(() => {
    const rows: Array<{ start: Date; end: Date }> = [];
    let hasMore = false;

    if (form.recurrencePreset === 'one_time') {
      rows.push({ start: form.startDateTime, end: closesAt });
      return { rows, hasMore };
    }

    if (form.recurrencePreset === 'custom') {
      return { rows, hasMore };
    }

    let cursor = new Date(form.startDateTime);
    while (rows.length < MAX_PREVIEW_OCCURRENCES) {
      if (form.repeatUntilMode === 'date' && effectiveRepeatUntilDate && cursor > effectiveRepeatUntilDate) {
        break;
      }
      rows.push({ start: new Date(cursor), end: addDays(cursor, form.durationDays) });
      cursor = nextStartForPreset(cursor, form.recurrencePreset);
    }

    if (form.repeatUntilMode !== 'date') {
      hasMore = rows.length > 0;
    } else if (effectiveRepeatUntilDate) {
      hasMore = cursor <= effectiveRepeatUntilDate;
    }

    return { rows, hasMore };
  }, [form.recurrencePreset, form.startDateTime, form.repeatUntilMode, form.repeatUntilDate, form.durationDays, closesAt, effectiveRepeatUntilDate]);
  const schedulePreviewLabels = useMemo(
    () => recurrencePreviewData.rows.map((item) => `${formatDateOnly(item.start)} - ${formatDateOnly(item.end)}`),
    [recurrencePreviewData.rows]
  );
  const primaryScheduleLabel = schedulePreviewLabels[0] || 'No upcoming surveys';
  const showMoreSchedules = schedulePreviewLabels.length > 1;
  const isSchedulePreviewTruncated = recurrencePreviewData.hasMore;
  const isSubmitting = isCreating || isUpdatingStructure || isCreatingQuestionSet || isUpdatingQuestionSet;
  const isTeamSelectionDisabled = isSubmitting || (isEditMode && isFetchingStructures);

  const handleOpenTeamModal = () => {
    if (isTeamSelectionDisabled) {
      return;
    }
    void openTeamModal();
  };

  const goBack = () => navigate('/app/pulse-surveys');

  const toggleLibraryQuestionSelection = (questionId: number) => {
    setSelectedLibraryQuestionIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const openCustomQuestionBuilder = () => {
    setQuestionSelectionMode('custom');
    setCustomQuestions((prev) => (prev.length > 0 ? prev : [createCustomQuestionDraft(0)]));
  };

  const addCustomQuestionRow = () => {
    setCustomQuestions((prev) => [...prev, createCustomQuestionDraft(prev.length)]);
  };

  const updateCustomQuestion = (localId: string, updates: Partial<Omit<CustomQuestionDraft, 'localId'>>) => {
    setCustomQuestions((prev) => prev.map((q) => (q.localId === localId ? { ...q, ...updates } : q)));
  };

  const addCustomQuestionOption = (localId: string) => {
    setCustomQuestions((prev) =>
      prev.map((question) =>
        question.localId === localId
          ? { ...question, options: [...(question.options || []), ''] }
          : question
      )
    );
  };

  const updateCustomQuestionOption = (localId: string, optionIndex: number, value: string) => {
    setCustomQuestions((prev) =>
      prev.map((question) => {
        if (question.localId !== localId) {
          return question;
        }

        const nextOptions = [...(question.options || [])];
        nextOptions[optionIndex] = value;
        return { ...question, options: nextOptions };
      })
    );
  };

  const removeCustomQuestionOption = (localId: string, optionIndex: number) => {
    setCustomQuestions((prev) =>
      prev.map((question) => {
        if (question.localId !== localId) {
          return question;
        }

        const nextOptions = (question.options || []).filter((_, index) => index !== optionIndex);
        return { ...question, options: nextOptions.length > 0 ? nextOptions : [''] };
      })
    );
  };

  const removeCustomQuestion = (localId: string) => {
    setCustomQuestions((prev) => prev.filter((q) => q.localId !== localId));
  };

  const applyQuestionSelectionFromModal = () => {
    if (!libraryQuestionSet) {
      addToast('Question library not found. Try again after refreshing.', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    const libraryQuestions = normalizeLibraryQuestions(libraryQuestionSet.questions);
    const selectedQuestions = libraryQuestions.filter((question) => selectedLibraryQuestionIds.includes(question.id));

    const invalidMultipleChoiceDraft = customQuestions.find(
      (question) => isMultipleChoiceQuestionType(question.type)
        && normalizeMultipleChoiceOptions(question.options).length < 2
        && question.question.trim().length > 0
    );
    if (invalidMultipleChoiceDraft) {
      addToast('Multiple choice questions require at least 2 options.', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    const customPayloadQuestions = customQuestions
      .map((question) => {
        const normalizedQuestion = question.question.trim();
        const options = normalizeMultipleChoiceOptions(question.options);
        const isMultipleChoice = isMultipleChoiceQuestionType(question.type);

        return {
          id: question.existingQuestionId,
          question: normalizedQuestion,
          pillar: null,
          type: question.type,
          theme: null,
          sort_order: 0,
          config: isMultipleChoice
            ? {
                render_mode:
                  question.type === QUESTION_TYPE_MULTIPLE_CHOICE_RADIO
                    ? 'radio'
                    : question.type === QUESTION_TYPE_MULTIPLE_CHOICE_CHECKBOX
                      ? 'checkbox'
                      : 'select',
                options,
                ...(question.type === QUESTION_TYPE_MULTIPLE_CHOICE_CHECKBOX
                  ? { max_selections: Math.max(1, Number(question.maxSelections || 3)) }
                  : {}),
              }
            : {},
          allow_feedback: question.type !== QUESTION_TYPE_FEEDBACK,
          required: false,
        };
      })
      .filter((question) => question.question.length > 0);

    if (selectedQuestions.length === 0 && customPayloadQuestions.length === 0) {
      addToast('Select at least one library question or add a custom question before saving.', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    const existingQuestionsByIdentity = new Map<string, DisplayQuestion[]>();
    addedQuestionsForTable.forEach((question) => {
      const key = questionIdentityKey(question);
      const bucket = existingQuestionsByIdentity.get(key) || [];
      bucket.push(question);
      existingQuestionsByIdentity.set(key, bucket);
    });

    const takeExistingQuestionByIdentity = (key: string) => {
      const bucket = existingQuestionsByIdentity.get(key) || [];
      if (bucket.length === 0) {
        return undefined;
      }
      const next = bucket.shift();
      existingQuestionsByIdentity.set(key, bucket);
      return next;
    };

    const takeExistingQuestionById = (id?: number) => {
      if (typeof id !== 'number') {
        return undefined;
      }
      const identityEntries = Array.from(existingQuestionsByIdentity.entries());
      for (const [key, bucket] of identityEntries) {
        const index = bucket.findIndex((question) => question.id === id);
        if (index !== -1) {
          const [match] = bucket.splice(index, 1);
          existingQuestionsByIdentity.set(key, bucket);
          return match;
        }
      }
      return undefined;
    };

    const payloadQuestions = [
      ...selectedQuestions.map((question) => {
        const matchedExisting = takeExistingQuestionByIdentity(questionIdentityKey(question));

        return {
          id: matchedExisting?.id,
          question: question.question,
          pillar: question.pillar ?? null,
          type: question.type ?? 1,
          theme: question.theme ?? null,
          sort_order: 0,
          config: matchedExisting?.config ?? question.config ?? {},
          allow_feedback: isFeedbackQuestionType(question.type)
            ? false
            : (matchedExisting?.allow_feedback ?? question.allow_feedback ?? true),
          required: matchedExisting?.required ?? question.required ?? false,
        };
      }),
      ...customPayloadQuestions.map((question) => {
        const matchedExisting = takeExistingQuestionById(question.id)
          || takeExistingQuestionByIdentity(questionIdentityKey(question));

        return {
          ...question,
          id: matchedExisting?.id ?? question.id,
          config: matchedExisting?.config ?? question.config,
          allow_feedback: isFeedbackQuestionType(question.type)
            ? false
            : (matchedExisting?.allow_feedback ?? question.allow_feedback ?? true),
          required: matchedExisting?.required ?? question.required ?? false,
        };
      }),
    ]
      .map((question, index) => ({
        ...question,
        sort_order: index + 1,
      }));

    const currentSetId = contextQuestionSetId || (contextQuestionSet?.id ?? null);

    setContextQuestionSetId(currentSetId);
    setAddedQuestionsPreview(payloadQuestions);
    setQuestionSettingsDirty(true);
    setIsQuestionModalOpen(false);
  };

  const submitCreate = async (
    payload: PulseScheduleStructurePayload,
    createAudience?: PulseScheduleStructureInviteAudience
  ) => {
    const created = await createPulseSurvey({ payload }).unwrap();
    const createdId = created.id;

    if (createdId && createAudience) {
      try {
        let inviteResult;
        if (createAudience.type === 'team') {
          inviteResult = await invitePulseParticipants({ id: createdId, body: { audience: 'team' } }).unwrap();
        } else {
          if (activeOrganizationMemberIds.length === 0) {
            addToast('Pulse survey created. No active organization members were found to invite.', {
              appearance: 'success',
              autoDismiss: true,
            });
            await refetch();
            goBack();
            return;
          }

          inviteResult = await invitePulseParticipants({
            id: createdId,
            body: { employee_ids: activeOrganizationMemberIds },
          }).unwrap();
        }

        addToast(
          `Pulse survey created. ${inviteResult.invited} invited, ${inviteResult.already_invited} already invited, ${inviteResult.not_eligible.length} not eligible.`,
          { appearance: 'success', autoDismiss: true }
        );
      } catch (inviteErr) {
        addToast(`Pulse survey created, but invites failed: ${errorDetail(inviteErr, 'Unable to invite participants.')}`, {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    } else {
      addToast('Pulse survey created.', { appearance: 'success', autoDismiss: true });
    }

    await refetch();
    goBack();
  };

  const onSave = async () => {
    if (isSaveLocked || isSubmitting) {
      return;
    }

    setIsSaveLocked(true);

    try {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }

    if (addedQuestionsForTable.length === 0) {
      setError('Select at least one question.');
      return;
    }

    if (!Number.isFinite(form.durationDays) || form.durationDays < 1) {
      setError('Duration must be at least 1 day.');
      return;
    }

    if (form.recurrencePreset === 'custom' && !form.recurrenceRule.trim()) {
      setError('Custom recurrence rule is required.');
      return;
    }

    if (!isRepeatUntilDateValid) {
      setError('Repeat until date must be after the first start date.');
      return;
    }

    if (form.reminderEnabled && !cronInfo.valid) {
      setError(cronInfo.error || 'Reminder schedule is invalid.');
      return;
    }

    let createAudience: PulseScheduleStructureInviteAudience | undefined;
    if (!isEditMode) {
      createAudience = normalizedSelectedTeamIds.length > 0 ? { type: 'team' } : { type: 'organization' };
    }

    try {
      setError(null);
      const questionsPayload = addedQuestionsForTable.map((question, index) => ({
        id: question.id,
        question: question.question,
        pillar: question.pillar ?? null,
        type: question.type ?? 1,
        theme: question.theme ?? null,
        sort_order: index + 1,
        config: question.config ?? {},
        allow_feedback: isFeedbackQuestionType(question.type)
          ? false
          : (question.allow_feedback ?? true),
        required: question.required ?? false,
      }));

      let questionSetId = form.assessmentQuestionSet ?? null;
      const mustCreateQuestionSet =
        questionSetId === null ||
        (questionSettingsDirty && questionSetId === (libraryQuestionSet?.id ?? null));

      if (mustCreateQuestionSet) {
        const title = buildGeneratedQuestionSetTitle(
          form.name,
          org?.name || org?.organizationName || org?.organization_name
        );
        const createdQuestionSet = await createPulseQuestionSet({
          payload: {
            type: QUESTION_SET_TYPE_PULSE,
            title,
            scope: QUESTION_SET_SCOPE_ORG,
            team: null,
            questions: questionsPayload,
          },
        }).unwrap();

        questionSetId = createdQuestionSet.id;
        setContextQuestionSetId(createdQuestionSet.id);
        setForm((prev) => ({ ...prev, assessmentQuestionSet: createdQuestionSet.id }));
        setAddedQuestionsPreview(
          createdQuestionSet.questions && createdQuestionSet.questions.length > 0
            ? createdQuestionSet.questions
            : questionsPayload
        );
        setQuestionSettingsDirty(false);
      } else if (questionSetId !== null && questionSettingsDirty) {
        const savedQuestionSet = await patchPulseQuestionSet({
          id: questionSetId,
          payload: {
            questions: questionsPayload,
          },
        }).unwrap();

        setAddedQuestionsPreview(savedQuestionSet.questions && savedQuestionSet.questions.length > 0 ? savedQuestionSet.questions : questionsPayload);
        setQuestionSettingsDirty(false);
      }

      if (!questionSetId) {
        setError('Question set is required.');
        return;
      }

      const recurrenceType = form.recurrencePreset === 'one_time' ? RECURS_ONETIME : RECURS_RRULE;
      const baseRule = form.recurrencePreset === 'custom'
        ? stripUntilClause(form.recurrenceRule.trim())
        : recurringBaseRuleForPreset(form.recurrencePreset);
      const recurrenceRule =
        recurrenceType === RECURS_RRULE
          ? [
              baseRule,
              form.repeatUntilMode === 'date' && form.repeatUntilDate
                ? `UNTIL=${untilAsRruleValue(effectiveRepeatUntilDate ?? form.repeatUntilDate)}`
                : null,
            ]
              .filter(Boolean)
              .join(';')
          : null;

      const payload: PulseScheduleStructurePayload = {
        name: form.name.trim(),
        team: normalizedSelectedTeamIds[0] ?? null,
        team_ids: normalizedSelectedTeamIds.length > 0 ? normalizedSelectedTeamIds : undefined,
        assessment_question_set: questionSetId,
        start_date_time: form.startDateTime.toISOString(),
        end_date_time: closesAt.toISOString(),
        duration_days: form.durationDays,
        reminder_enabled: form.reminderEnabled,
        reminder_schedule: form.reminderSchedule || null,
        recurrence_type: recurrenceType,
        recurrence_rule: recurrenceRule,
        is_active: form.isActive,
      };

      if (isEditMode) {
        if (!parsedStructureId) {
          setError('Invalid pulse survey id.');
          return;
        }

        await updatePulseSurveyStructure({ id: parsedStructureId, payload }).unwrap();
        addToast('Pulse survey updated.', { appearance: 'success', autoDismiss: true });
        await refetch();
        goBack();
        return;
      }

      await submitCreate(payload, createAudience);
    } catch (err) {
      setError(errorDetail(err, 'Failed to save pulse survey.'));
    }
    } finally {
      setIsSaveLocked(false);
    }
  };

  if (isEditMode && isLoadingStructures) {
    return (
      <PageContent data-testid="pulse-structure-editor-page">
        <PageSection>
          <LoadingSpinner />
        </PageSection>
      </PageContent>
    );
  }

  if (isEditMode && !structure) {
    return (
      <PageContent data-testid="pulse-structure-editor-page">
        <PageHeader title="Edit Pulse Survey" />
        <PageSection>
          <Message negative content="Pulse survey not found." />
        </PageSection>
      </PageContent>
    );
  }

  return (
    <PageContent data-testid="pulse-structure-editor-page">
      <PageHeader
        title={isEditMode ? (form.name.trim() || structure?.name || 'Edit Pulse Survey') : 'Create Pulse Survey'}
        controls={(
          <HeaderActionBar>
            <StyledButton
              label="Cancel"
              color="gray"
              width="110px"
              disabled={isSubmitting}
              onClick={goBack}
            />
            <StyledSaveButton
              label={isSubmitting || isSaveLocked ? 'Saving...' : isEditMode ? 'Save' : 'Create'}
              width="110px"
              disabled={isSubmitting || isSaveLocked}
              onClick={onSave}
            />
          </HeaderActionBar>
        )}
      />

      <PageSection>
        <Card>
          <Form>
          {!isEditMode && (
            <FormRow>
              <CompactFieldLabel>Name</CompactFieldLabel>
              <NameFieldControl>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Pulse Survey Name"
                  required
                />
              </NameFieldControl>
            </FormRow>
          )}

          <FormRow
            onClick={handleOpenTeamModal}
            style={{ cursor: isTeamSelectionDisabled ? 'default' : 'pointer' }}
          >
            <CompactFieldLabel>Teams</CompactFieldLabel>
            <FieldControl>
              <TeamSelectorControl>
                <TeamSelectorSummary title={selectedTeamsSummary.title}>
                  <TeamSelectorSummaryText>{selectedTeamsSummary.text}</TeamSelectorSummaryText>
                  {selectedTeamsSummary.overflow > 0 && (
                    <TeamOverflowPill>{`+${selectedTeamsSummary.overflow} more`}</TeamOverflowPill>
                  )}
                </TeamSelectorSummary>
                <TeamSelectorActions>
                  <StyledButton
                    label="Select"
                    color="gray"
                    width="80px"
                    disabled={isTeamSelectionDisabled}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenTeamModal();
                    }}
                  />
                </TeamSelectorActions>
              </TeamSelectorControl>
            </FieldControl>
          </FormRow>

          <CompactScheduleSection>
                <CompactGridRow1>
                  <CompactField>
                    <InlineField>
                      <InlineFieldLabel>Starts</InlineFieldLabel>
                      <InlineFieldControl>
                        <StyledDateTimeInput
                          value={form.startDateTime}
                          onChange={(value) => {
                            if (!value) return;
                            setForm((prev) => ({
                              ...prev,
                              startDateTime: value,
                            }));
                          }}
                          hasValue={!!form.startDateTime}
                        />
                      </InlineFieldControl>
                    </InlineField>
                  </CompactField>

                  <CompactField>
                    <InlineField>
                      <InlineFieldLabel>Open for</InlineFieldLabel>
                      <InlineFieldControl>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: 'auto' }}>
                          <Input
                            style={{ width: 84 }}
                            type="number"
                            min={1}
                            step={1}
                            value={form.durationDays}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const parsed = Number(e.target.value);
                              const nextDuration = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
                              setForm((prev) => ({
                                ...prev,
                                durationDays: nextDuration,
                              }));
                            }}
                          />
                          <span style={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14, lineHeight: '18px', color: '#333' }}>
                            days
                          </span>
                        </div>
                      </InlineFieldControl>
                    </InlineField>
                  </CompactField>
                </CompactGridRow1>

                <CompactGridRow2>
                  <CompactField>
                    <InlineField>
                      <InlineFieldLabel>Recurrence</InlineFieldLabel>
                      <InlineFieldControl>
                        <Dropdown
                          fluid
                          selection
                          options={RECURRENCE_PRESET_OPTIONS}
                          value={form.recurrencePreset}
                          onChange={(_, data) =>
                            setForm((prev) => {
                              const nextPreset = (data.value as RecurrencePreset) || 'one_time';
                              return {
                                ...prev,
                                recurrencePreset: nextPreset,
                                repeatUntilMode: nextPreset === 'one_time' ? 'none' : prev.repeatUntilMode,
                              };
                            })
                          }
                        />
                        {(form.recurrencePreset as string) === 'custom' && (
                          <Input
                            style={{ marginTop: 8 }}
                            value={form.recurrenceRule}
                            placeholder="FREQ=WEEKLY;INTERVAL=1;BYDAY=MO"
                            onChange={(e) => setForm((prev) => ({ ...prev, recurrenceRule: e.target.value }))}
                          />
                        )}
                      </InlineFieldControl>
                    </InlineField>
                  </CompactField>

                  <CompactField>
                    <InlineField>
                      <InlineFieldLabel>{form.recurrencePreset === 'one_time' ? 'Schedule' : 'Repeat until'}</InlineFieldLabel>
                      <InlineFieldControl>
                        {form.recurrencePreset === 'one_time' ? (
                          <CompactSummaryArea>
                            <SchedulePreviewBox>
                              <SchedulePreviewRow>
                                <SchedulePrimaryLine title={primaryScheduleLabel}>{primaryScheduleLabel}</SchedulePrimaryLine>
                                {showMoreSchedules && (
                                  <Popup
                                    hoverable
                                    position="bottom left"
                                    on="hover"
                                    trigger={(
                                      <MoreSchedulesButton type="button">
                                        + more
                                      </MoreSchedulesButton>
                                    )}
                                  >
                                    <SchedulePopupList>
                                      {schedulePreviewLabels.map((label, index) => (
                                        <PreviewLine key={`${label}-${index}`}>{label}</PreviewLine>
                                      ))}
                                      {isSchedulePreviewTruncated && <PreviewEllipsisLine>...</PreviewEllipsisLine>}
                                    </SchedulePopupList>
                                  </Popup>
                                )}
                              </SchedulePreviewRow>
                            </SchedulePreviewBox>
                          </CompactSummaryArea>
                        ) : (
                          <RepeatUntilControlsRow>
                            <Dropdown
                              fluid
                              selection
                              options={REPEAT_UNTIL_OPTIONS}
                              value={form.repeatUntilMode}
                              onChange={(_, data) => {
                                const mode = (data.value as RepeatUntilMode) || 'none';
                                setForm((prev) => ({
                                  ...prev,
                                  repeatUntilMode: mode,
                                  repeatUntilDate: mode === 'none' ? null : prev.repeatUntilDate ?? closesAt,
                                }));
                              }}
                            />
                            {form.repeatUntilMode === 'date' && (
                              <StyledDateTimeInput
                                value={effectiveRepeatUntilDate ?? closesAt}
                                onChange={(value) => {
                                  if (!value) return;
                                  setForm((prev) => ({
                                    ...prev,
                                    repeatUntilDate: value,
                                  }));
                                }}
                                hasValue={Boolean(effectiveRepeatUntilDate)}
                              />
                            )}
                          </RepeatUntilControlsRow>
                        )}
                        {form.recurrencePreset !== 'one_time' && !isRepeatUntilDateValid && (
                          <ErrorText>Repeat until date must be after the first start date.</ErrorText>
                        )}
                      </InlineFieldControl>
                    </InlineField>
                  </CompactField>
                </CompactGridRow2>

                {form.recurrencePreset !== 'one_time' && (
                  <CompactGridRowRepeat>
                    <CompactField>
                      <InlineField>
                        <InlineFieldLabel>Upcoming surveys</InlineFieldLabel>
                        <InlineFieldControl>
                          <CompactSummaryArea>
                            <SchedulePreviewBox>
                              {form.recurrencePreset === 'custom' ? (
                                <PreviewLine>Preview unavailable for custom recurrence.</PreviewLine>
                              ) : (
                                <SchedulePreviewRow>
                                  <SchedulePrimaryLine title={primaryScheduleLabel}>{primaryScheduleLabel}</SchedulePrimaryLine>
                                  {showMoreSchedules && (
                                    <Popup
                                      hoverable
                                      position="bottom left"
                                      on="hover"
                                      trigger={(
                                        <MoreSchedulesButton type="button">
                                          + more
                                        </MoreSchedulesButton>
                                      )}
                                    >
                                      <SchedulePopupList>
                                        {schedulePreviewLabels.map((label, index) => (
                                          <PreviewLine key={`${label}-${index}`}>{label}</PreviewLine>
                                        ))}
                                        {isSchedulePreviewTruncated && <PreviewEllipsisLine>...</PreviewEllipsisLine>}
                                      </SchedulePopupList>
                                    </Popup>
                                  )}
                                </SchedulePreviewRow>
                              )}
                            </SchedulePreviewBox>
                          </CompactSummaryArea>
                        </InlineFieldControl>
                      </InlineField>
                    </CompactField>
                    <CompactField>
                      <InlineField>
                        <InlineFieldLabel>Remiders</InlineFieldLabel>
                        <InlineFieldControl>
                          <ReminderContentWrap>
                            <ReminderHeaderRow>
                              <PlainEnglishText
                                $valid={cronInfo.valid}
                                onClick={() => setShowReminderEditor(true)}
                                title="Click to edit reminder schedule"
                              >
                                {!form.reminderEnabled
                                  ? 'Automated reminders disabled'
                                  : cronInfo.valid
                                    ? cronInfo.plainEnglish
                                    : cronInfo.error || 'Invalid schedule expression'}
                              </PlainEnglishText>

                              <ButtonRow>
                                <StyledIconButton
                                  icon={form.reminderEnabled ? 'bell' : 'bell slash'}
                                  title={form.reminderEnabled ? 'Disable Reminders' : 'Enable Reminders'}
                                  color={form.reminderEnabled ? 'green' : 'grey'}
                                  onClick={() => setForm((prev) => ({ ...prev, reminderEnabled: !prev.reminderEnabled }))}
                                  disabled={false}
                                />
                                <StyledIconButton
                                  icon="edit"
                                  title="Edit Schedule"
                                  onClick={() => setShowReminderEditor(true)}
                                  disabled={false}
                                />
                              </ButtonRow>
                            </ReminderHeaderRow>
                          </ReminderContentWrap>
                        </InlineFieldControl>
                      </InlineField>
                    </CompactField>
                  </CompactGridRowRepeat>
                )}
          </CompactScheduleSection>

          {form.recurrencePreset === 'one_time' && (
            <FormRow>
              <CompactFieldLabel>Remiders</CompactFieldLabel>
              <FieldControl>
                <ReminderContentWrap>
                  <ReminderHeaderRow>
                    <PlainEnglishText
                      $valid={cronInfo.valid}
                      onClick={() => setShowReminderEditor(true)}
                      title="Click to edit reminder schedule"
                    >
                      {!form.reminderEnabled
                        ? 'Automated reminders disabled'
                        : cronInfo.valid
                          ? cronInfo.plainEnglish
                          : cronInfo.error || 'Invalid schedule expression'}
                    </PlainEnglishText>

                    <ButtonRow>
                      <StyledIconButton
                        icon={form.reminderEnabled ? 'bell' : 'bell slash'}
                        title={form.reminderEnabled ? 'Disable Reminders' : 'Enable Reminders'}
                        color={form.reminderEnabled ? 'green' : 'grey'}
                        onClick={() => setForm((prev) => ({ ...prev, reminderEnabled: !prev.reminderEnabled }))}
                        disabled={false}
                      />
                      <StyledIconButton
                        icon="edit"
                        title="Edit Schedule"
                        onClick={() => setShowReminderEditor(true)}
                        disabled={false}
                      />
                    </ButtonRow>
                  </ReminderHeaderRow>
                </ReminderContentWrap>
              </FieldControl>
            </FormRow>
          )}

          {error && <Message negative content={error} />}
          </Form>
        </Card>

        <SurveyQuestionsSection>
          <PageSection display="flex" justifyContent="space-between" padtop padbottom='10px'>
            <Subtitle>Survey Questions</Subtitle>
            <StyledAddButton
              label="Manage Questions"
              width="170px"
              disabled={isSubmitting}
              onClick={() => setIsQuestionModalOpen(true)}
            />
          </PageSection>

          <AddedQuestionsSection>
          {addedQuestionsForTable.length === 0 ? (
            <Message info content="No questions have been added to this pulse survey yet." />
          ) : (
            <QuestionsTable>
              <TableHeader>
                <TableRow>
                  <HeaderCell width={1} />
                  <HeaderCell width={7}>Question</HeaderCell>
                  <HeaderCell width={2}>Type</HeaderCell>
                  <HeaderCell width={3}>Pillar</HeaderCell>
                  <HeaderCell width={3} justify="center">Options</HeaderCell>
                  <HeaderCell width={2} justify="center">Actions</HeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {addedQuestionsForTable.map((question, index) => (
                  <AddedQuestionsTableRow
                    key={`${question.id ?? 'question'}-${index}`}
                    onDragOver={(event: { preventDefault: () => void; }) => {
                      event.preventDefault();
                    }}
                    onDrop={() => {
                      if (draggedQuestionIndex === null) {
                        return;
                      }
                      moveQuestionRow(draggedQuestionIndex, index);
                      setDraggedQuestionIndex(null);
                    }}
                  >
                    <DragOrderTableCell aria-label={`Drag to reorder question ${index + 1}`} justify="center">
                      <DragOrderContent>
                        <DragHandle
                          draggable={!isSubmitting}
                          onDragStart={() => setDraggedQuestionIndex(index)}
                          onDragEnd={() => setDraggedQuestionIndex(null)}
                          title={`Drag to reorder question ${index + 1}`}
                          aria-label={`Drag handle for question ${index + 1}`}
                        >
                          ⋮⋮
                        </DragHandle>
                        <DragOrderNumber>{index + 1}</DragOrderNumber>
                      </DragOrderContent>
                    </DragOrderTableCell>
                    <TableCell>
                      <QuestionCellText>{question.question}</QuestionCellText>
                      <QuestionSubheading>{getThemeLabel(question.theme)}</QuestionSubheading>
                    </TableCell>
                    <TableCell>
                      <QuestionTypeText>{getQuestionTypeLabel(question.type)}</QuestionTypeText>
                    </TableCell>
                    <TableCell>
                      <PillarCellText>
                        {getPillarLabel(question.pillar) === 'Unassigned' ? '--' : getPillarLabel(question.pillar)}
                      </PillarCellText>
                    </TableCell>
                    <TableCell justify="center">
                      <OptionsCellContent>
                        {isFeedbackQuestionType(question.type) ? (
                          <StateIconButton type="button" title="Feedback enabled" disabled aria-label="Feedback enabled">
                            <Icon name="comment alternate" color="teal" />
                          </StateIconButton>
                        ) : (
                          <StateIconButton
                            type="button"
                            title={Boolean(question.allow_feedback ?? true) ? 'Feedback enabled' : 'Feedback disabled'}
                            aria-label={Boolean(question.allow_feedback ?? true) ? 'Feedback enabled' : 'Feedback disabled'}
                            disabled={isSubmitting}
                            onClick={() => setQuestionFlag(index, 'allow_feedback', !Boolean(question.allow_feedback ?? true))}
                          >
                            <Icon
                              name={Boolean(question.allow_feedback ?? true) ? 'comment alternate' : 'comment outline'}
                              color={Boolean(question.allow_feedback ?? true) ? 'teal' : 'grey'}
                            />
                          </StateIconButton>
                        )}
                        <StateIconButton
                          type="button"
                          title={Boolean(question.required) ? 'Response required' : 'Response optional'}
                          aria-label={Boolean(question.required) ? 'Response required' : 'Response optional'}
                          disabled={isSubmitting}
                          onClick={() => setQuestionFlag(index, 'required', !Boolean(question.required))}
                        >
                          <Icon
                            name={Boolean(question.required) ? 'asterisk' : 'circle outline'}
                            color={Boolean(question.required) ? 'orange' : 'grey'}
                          />
                        </StateIconButton>
                      </OptionsCellContent>
                    </TableCell>
                    <TableCell justify="center">
                      <RowActionsWrap>
                        <Dropdown
                          icon="ellipsis vertical"
                          floating
                          className="row-actions-kebab icon"
                          direction="left"
                          title={`Actions for question ${index + 1}`}
                          style={{ width: '34px', height: '32px', borderRadius: '8px', padding: 0 }}
                        >
                          <Dropdown.Menu>
                            <Dropdown.Item
                              icon={question.required ? 'check square outline' : 'square outline'}
                              text="Make Response Required"
                              onClick={() => setQuestionFlag(index, 'required', !Boolean(question.required))}
                            />
                            {!isFeedbackQuestionType(question.type) && (
                              <Dropdown.Item
                                icon={Boolean(question.allow_feedback ?? true) ? 'check square outline' : 'square outline'}
                                text="Allow Feedback"
                                onClick={() => setQuestionFlag(index, 'allow_feedback', !Boolean(question.allow_feedback ?? true))}
                              />
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </RowActionsWrap>
                    </TableCell>
                  </AddedQuestionsTableRow>
                ))}
              </TableBody>
            </QuestionsTable>
          )}
          </AddedQuestionsSection>
        </SurveyQuestionsSection>
      </PageSection>

      <PositionedModal
        open={isTeamModalOpen}
        onClose={closeTeamModal}
        size="small"
        closeIcon
        style={{ height: TEAM_SELECTION_MODAL_HEIGHT, display: 'flex', flexDirection: 'column' }}
      >
        <Modal.Header>Select Teams</Modal.Header>
        <TeamModalContent>
          <TeamSearchWrap>
            <Input
              fluid
              icon="search"
              placeholder="Search teams"
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
            />
          </TeamSearchWrap>

          <TeamBulkActions>
            <Button basic size="tiny" onClick={selectAllTeams}>
              Select all
            </Button>
            <Button basic size="tiny" onClick={clearModalTeamSelection}>
              Clear
            </Button>
            <TeamSelectedCount>{normalizedModalSelectedTeamIds.length} selected</TeamSelectedCount>
          </TeamBulkActions>

          <TeamChecklist>
            {filteredTeams.length === 0 ? (
              <Message info content="No teams match your search." />
            ) : (
              filteredTeams.map((team) => (
                <TeamChecklistItem key={team.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={modalSelectedTeamIdSet.has(team.id)}
                      onChange={() => toggleTeamSelection(team.id)}
                    />
                    <span>{team.name}</span>
                  </label>
                </TeamChecklistItem>
              ))
            )}
          </TeamChecklist>
        </TeamModalContent>
        <Modal.Actions>
          <ModalActionsRow>
            <StyledButton
              label="Done"
              color="gray"
              width="110px"
              onClick={applyTeamModalSelection}
            />
          </ModalActionsRow>
        </Modal.Actions>
      </PositionedModal>

      <PositionedModal
        open={showReminderEditor}
        onClose={() => setShowReminderEditor(false)}
        size="small"
        closeIcon
      >
        <Modal.Header>Edit Reminder Schedule</Modal.Header>
        <Modal.Content>
          <ReminderSection>
            <CronExpressionInput
              value={form.reminderSchedule}
              onChange={(value) => setForm((prev) => ({ ...prev, reminderSchedule: value ?? '' }))}
              placeholder="0 9 */2 * *"
              timezone={orgTimezone}
              showAdvancedModes={showAdvancedReminderModes}
            />
          </ReminderSection>
        </Modal.Content>
        <Modal.Actions>
          <StyledButton
            label="Close"
            color="gray"
            width="110px"
            onClick={() => setShowReminderEditor(false)}
          />
        </Modal.Actions>
      </PositionedModal>

      <PositionedModal
        open={isQuestionModalOpen}
        onClose={() => setIsQuestionModalOpen(false)}
        size="large"
        closeIcon
      >
        <Modal.Header>Select Questions</Modal.Header>
        <Modal.Content>
          <LibraryModalContent>
            <LibraryModalLayout>
            <ThemeColumn>
              <LibraryFilterToggle>
                <LibraryFilterToggleButton
                  type="button"
                  $active={libraryFilterMode === 'themes'}
                  onClick={() => {
                    setQuestionSelectionMode('library');
                    setLibraryFilterMode('themes');
                    setActiveThemeId((prev) => prev || THEME_OPTIONS[0].id);
                  }}
                >
                  Themes
                </LibraryFilterToggleButton>
                <LibraryFilterToggleButton
                  type="button"
                  $active={libraryFilterMode === 'pillars'}
                  onClick={() => {
                    setQuestionSelectionMode('library');
                    setLibraryFilterMode('pillars');
                    setActivePillarId((prev) => prev || (PILLARS[0]?.id ?? 0));
                  }}
                >
                  Pillars
                </LibraryFilterToggleButton>
              </LibraryFilterToggle>

              {libraryFilterMode === 'themes'
                ? THEME_OPTIONS.map((theme) => (
                    <ThemeButton
                      key={theme.id}
                      type="button"
                      $active={questionSelectionMode === 'library' && activeThemeId === theme.id}
                      onClick={() => {
                        setQuestionSelectionMode('library');
                        setActiveThemeId(theme.id);
                      }}
                    >
                      <ThemeButtonHeader>
                        <ThemeButtonLabel>{theme.label}</ThemeButtonLabel>
                        {selectedQuestionCountByTheme[theme.id] > 0 && (
                          <ThemeButtonCount>
                          {selectedQuestionCountByTheme[theme.id]} selected
                          </ThemeButtonCount>
                        )}
                      </ThemeButtonHeader>
                    </ThemeButton>
                  ))
                : PILLARS.map((pillar) => (
                    <ThemeButton
                      key={pillar.id}
                      type="button"
                      $active={questionSelectionMode === 'library' && activePillarId === pillar.id}
                      onClick={() => {
                        setQuestionSelectionMode('library');
                        setActivePillarId(pillar.id);
                      }}
                    >
                      <ThemeButtonHeader>
                        <ThemeButtonLabel>{pillar.name}</ThemeButtonLabel>
                        {selectedQuestionCountByPillar[pillar.id] > 0 && (
                          <ThemeButtonCount>
                          {selectedQuestionCountByPillar[pillar.id]} selected
                          </ThemeButtonCount>
                        )}
                      </ThemeButtonHeader>
                    </ThemeButton>
                  ))}

              <CustomQuestionAction>
                <ThemeButton
                  type="button"
                  $active={questionSelectionMode === 'custom'}
                  onClick={openCustomQuestionBuilder}
                >
                  <ThemeButtonHeader>
                    <ThemeButtonLabel>Custom Questions</ThemeButtonLabel>
                    {selectedCustomQuestionCount > 0 && (
                      <ThemeButtonCount>
                      {selectedCustomQuestionCount} selected
                      </ThemeButtonCount>
                    )}
                  </ThemeButtonHeader>
                </ThemeButton>
              </CustomQuestionAction>
            </ThemeColumn>

            <QuestionsColumn>
              {questionSelectionMode === 'custom' ? (
                <CustomQuestionBuilder>
                  <BuilderHeaderRow>
                    <BuilderTitle>Build Custom Questions</BuilderTitle>
                    <StyledAddButton label="New Question" width="150px" onClick={addCustomQuestionRow} />
                  </BuilderHeaderRow>
                  {customQuestions.length === 0 ? (
                    <Message info content="Start by adding a custom question." />
                  ) : (
                    <CustomQuestionList>
                      {customQuestions.map((question, index) => (
                        <CustomQuestionCard key={question.localId}>
                          <CustomQuestionRowHeader>
                            <CustomQuestionOrdinal>Question {index + 1}</CustomQuestionOrdinal>
                          </CustomQuestionRowHeader>
                          <Form>
                            <CustomQuestionFieldRow>
                              <CustomQuestionFieldLabel>Question text</CustomQuestionFieldLabel>
                              <CustomQuestionFieldControl>
                                <Input
                                  placeholder="Type your custom question"
                                  value={question.question}
                                  onChange={(e) => updateCustomQuestion(question.localId, { question: e.target.value })}
                                />
                              </CustomQuestionFieldControl>
                            </CustomQuestionFieldRow>
                            <CustomQuestionFieldRow>
                              <CustomQuestionFieldLabel>Question type</CustomQuestionFieldLabel>
                              <QuestionTypeFieldControl>
                                <QuestionTypeControlRow>
                                  <QuestionTypeDropdownWrap>
                                    <Dropdown
                                      fluid
                                      selection
                                      options={QUESTION_TYPE_OPTIONS}
                                      value={question.type}
                                      onChange={(_, data) => {
                                        const nextType = Number(data.value) || QUESTION_TYPE_LIKERT;
                                        updateCustomQuestion(question.localId, {
                                          type: nextType,
                                          options: isMultipleChoiceQuestionType(nextType)
                                            ? (question.options.length > 0 ? question.options : ['', ''])
                                            : question.options,
                                          maxSelections: isCheckboxQuestionType(nextType)
                                            ? Math.max(1, Number(question.maxSelections || 3))
                                            : question.maxSelections,
                                        });
                                      }}
                                    />
                                  </QuestionTypeDropdownWrap>
                                  <SubtleTrashButton
                                    type="button"
                                    aria-label="Remove"
                                    title="Remove"
                                    onClick={() => removeCustomQuestion(question.localId)}
                                    disabled={customQuestions.length === 1}
                                  >
                                    <Icon name="trash alternate outline" />
                                  </SubtleTrashButton>
                                </QuestionTypeControlRow>
                              </QuestionTypeFieldControl>
                            </CustomQuestionFieldRow>
                            {isMultipleChoiceQuestionType(question.type) && (
                              <CustomQuestionFieldRow>
                                <CustomQuestionFieldLabel>Options</CustomQuestionFieldLabel>
                                <CustomQuestionFieldControl>
                                  <MultipleChoiceOptionsWrap>
                                    {question.options.map((option, optionIndex) => (
                                      <MultipleChoiceOptionRow key={`${question.localId}-option-${optionIndex}`}>
                                        <Input
                                          placeholder={`Option ${optionIndex + 1}`}
                                          value={option}
                                          onChange={(e) =>
                                            updateCustomQuestionOption(
                                              question.localId,
                                              optionIndex,
                                              e.target.value
                                            )
                                          }
                                        />
                                        <SubtleTrashButton
                                          type="button"
                                          aria-label="Remove option"
                                          title="Remove option"
                                          onClick={() =>
                                            removeCustomQuestionOption(question.localId, optionIndex)
                                          }
                                          disabled={question.options.length <= 2}
                                        >
                                          <Icon name="trash alternate outline" />
                                        </SubtleTrashButton>
                                      </MultipleChoiceOptionRow>
                                    ))}
                                    <OptionActionRow>
                                      <Button
                                        type="button"
                                        basic
                                        size="mini"
                                        onClick={() => addCustomQuestionOption(question.localId)}
                                      >
                                        Add option
                                      </Button>
                                    </OptionActionRow>
                                    {isCheckboxQuestionType(question.type) && (
                                      <OptionActionRow>
                                        <Input
                                          type="number"
                                          min={1}
                                          max={question.options.length || 1}
                                          value={Math.max(1, Number(question.maxSelections || 3))}
                                          onChange={(e) => {
                                            const nextValue = Math.max(1, Number(e.target.value || 1));
                                            updateCustomQuestion(question.localId, {
                                              maxSelections: Math.min(nextValue, Math.max(1, question.options.length || 1)),
                                            });
                                          }}
                                          label="Max selections"
                                          labelPosition="left"
                                        />
                                      </OptionActionRow>
                                    )}
                                  </MultipleChoiceOptionsWrap>
                                </CustomQuestionFieldControl>
                              </CustomQuestionFieldRow>
                            )}
                          </Form>
                        </CustomQuestionCard>
                      ))}
                    </CustomQuestionList>
                  )}
                </CustomQuestionBuilder>
              ) : activeLibraryQuestions.length === 0 ? (
                <Message info content="No questions are available for this selection yet." />
              ) : (
                <QuestionList>
                  {activeLibraryQuestions.map((question) => (
                    <QuestionRow key={question.id}>
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedLibraryQuestionIds.includes(question.id)}
                          onChange={() => toggleLibraryQuestionSelection(question.id)}
                        />
                        <QuestionTextBlock>
                          <QuestionText>{question.question}</QuestionText>
                          <QuestionRowSubtitle>
                            {libraryFilterMode === 'themes'
                              ? getPillarLabel(question.pillar)
                              : getThemeLabel(question.theme)}
                          </QuestionRowSubtitle>
                        </QuestionTextBlock>
                      </label>
                    </QuestionRow>
                  ))}
                </QuestionList>
              )}
            </QuestionsColumn>
            </LibraryModalLayout>
          </LibraryModalContent>
        </Modal.Content>
        <Modal.Actions>
          <ModalActionsRow>
            <QuestionModalSelectedCount>
              {totalSelectedQuestionCount} question{totalSelectedQuestionCount === 1 ? '' : 's'} selected
            </QuestionModalSelectedCount>
            <StyledButton
              label="Cancel"
              color="gray"
              width="110px"
              disabled={isSubmitting}
              onClick={() => setIsQuestionModalOpen(false)}
            />
            <StyledButton
              label="OK"
              color="blue"
              width="110px"
              disabled={isSubmitting}
              onClick={applyQuestionSelectionFromModal}
            />
          </ModalActionsRow>
        </Modal.Actions>
      </PositionedModal>
    </PageContent>
  );
};

export default PulseScheduleStructureEditorPage;

const FieldControl = styled.div`
  flex: 1;
  min-width: 0;

  & > * {
    width: 100%;
  }
`;

const NameFieldControl = styled(FieldControl)`
  flex: 0 1 650px;
  width: 100%;
  max-width: 650px;
`;

const TeamSelectorControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 8px 10px;
  background: #f8fafc;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TeamSelectorSummary = styled.div`
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #1f2937;
  line-height: 1.35;
`;

const TeamSelectorSummaryText = styled.span`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TeamOverflowPill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: #344054;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  padding: 4px 8px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const TeamSelectorActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`;


const TeamModalContent = styled(Modal.Content)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const TeamSearchWrap = styled.div`
  margin-bottom: 10px;
`;

const TeamBulkActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const TeamSelectedCount = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: #667085;
`;

const TeamChecklist = styled.div`
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  padding: 6px;
  overflow-y: auto;
  min-height: 0;
  flex: 1 1 auto;
`;

const TeamChecklistItem = styled.div`
  border-radius: 6px;
  padding: 8px;

  &:hover {
    background: #f8fafc;
  }

  label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    color: #1f2937;
    font-size: 13px;
  }
`;

const CompactFieldLabel = styled(FieldLabel)`
  width: 220px;

  @media (max-width: 1200px) {
    width: 150px;
  }
`;

const CompactScheduleSection = styled.div`
  margin-bottom: 20px;
`;

const CompactGridRow1 = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px 32px;
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const CompactGridRow2 = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px 32px;
  align-items: start;
  margin-top: 8px;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const CompactGridRowRepeat = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px 32px;
  align-items: start;
  margin-top: 8px;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const CompactField = styled.div`
  width: 100%;
`;

const InlineField = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  column-gap: 0;
  align-items: center;

  @media (max-width: 1200px) {
    grid-template-columns: 150px minmax(0, 1fr);
  }

  @media (max-width: 700px) {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 6px;
  }
`;

const InlineFieldLabel = styled.div`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #333;
  margin-bottom: 0;
  padding-top: 0;
`;

const InlineFieldControl = styled.div`
  min-width: 0;

  & > * {
    width: 100%;
  }
`;

const CompactSummaryArea = styled.div`
  margin-top: 0;
`;

const ErrorText = styled.div`
  color: #db2828;
  font-size: 12px;
  margin-top: 4px;
`;

const ReminderSection = styled.div`
  margin-top: 12px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const ReminderContentWrap = styled.div`
  width: 100%;
`;

const ReminderHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 900px) {
    flex-wrap: wrap;
    align-items: stretch;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const CalculatedValue = styled.div`
  padding: 9px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #f8fafc;
  color: #344054;
  font-size: 13px;
  font-weight: 500;
`;

const RepeatUntilWrap = styled.div`
  margin-top: 8px;
`;

const RepeatUntilControlsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 190px) minmax(0, 1fr);
  gap: 8px;
  align-items: center;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SchedulePreviewBox = styled.div`
  border: 1px solid #d0d5dd;
  border-radius: 4px;
  padding: 8px 10px;
  background: #f8fafc;
`;

const SchedulePreviewRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const SchedulePrimaryLine = styled.div`
  color: #1f2937;
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MoreSchedulesButton = styled.button`
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: #344054;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1;
  padding: 4px 8px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #f1f5f9;
  }
`;

const SchedulePopupList = styled.div`
  min-width: 260px;
  max-width: 360px;
`;

const PreviewLine = styled.div`
  color: #1f2937;
  font-size: 13px;
  line-height: 1.4;

  & + & {
    margin-top: 4px;
  }
`;

const PreviewEllipsisLine = styled.div`
  color: #98a2b3;
  font-size: 13px;
  line-height: 1.2;
  margin-top: 4px;
`;

const PlainEnglishText = styled.div<{ $valid: boolean }>`
  flex: 1;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;

  ${({ $valid }) => {
    if (!$valid) {
      return `
        color: #c62828;
        background: #ffebee;
        border: 1px solid #ef9a9a;
      `;
    }

    return `
      color: #344054;
      background: #f8fafc;
      border: 1px solid #d0d5dd;
    `;
  }}
`;

const HeaderActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const SurveyQuestionsSection = styled.div`
  margin-top: 20px;
`;


const AddedQuestionsSection = styled.div`
  margin-top: 0;
`;

const QuestionsTable = styled(Table)`
  && {
    width: 100%;
  }
`;

const AddedQuestionsTableRow = styled(TableRow)`
`;

const DragOrderTableCell = styled(TableCell)`
  && {
    width: 36px;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
`;

const DragOrderContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const DragHandle = styled.span`
  color: #98a2b3;
  font-size: 13px;
  user-select: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const DragOrderNumber = styled.span`
  font-size: 12px;
  color: #475467;
`;

const QuestionSubheading = styled.div`
  margin-top: 2px;
  font-size: 11px;
  line-height: 1.2;
  color: #98a2b3;
`;

const QuestionCellText = styled.div`
  font-weight: 600;
  color: #1f2937;
  line-height: 1.35;
`;

const PillarCellText = styled.div`
  font-weight: 400;
  color: #344054;
  font-size: 13px;
  line-height: 1.35;
`;

const QuestionTypeText = styled.div`
  font-weight: 400;
  color: #344054;
  font-size: 13px;
  line-height: 1.35;
`;

const StateIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  width: 30px;
  height: 30px;
  line-height: 1;
  color: #667085;
  border: 0;
  background: transparent;
  border-radius: 8px;
  padding: 0;
  transition: background-color 140ms ease, box-shadow 140ms ease;

  & > .icon {
    margin: 0 !important;
    line-height: 1;
    font-size: 15px;
  }

  &:not(:disabled) {
    cursor: pointer;

    &:hover {
      background: #f8fafc;
      box-shadow: inset 0 0 0 1px #d0d5dd;
    }
  }

  &:disabled {
    cursor: default;
  }
`;

const OptionsCellContent = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const RowActionsWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  & .row-actions-kebab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
  }

  & .row-actions-kebab > .dropdown.icon {
    margin: 0 !important;
    line-height: 1;
    transform: translateY(1px);
  }
`;

const LibraryModalLayout = styled.div`
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 18px;
  height: 100%;
  min-height: 0;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const LibraryModalContent = styled.div`
  height: min(70vh, 680px);
  min-height: 0;
  overflow: visible;
`;

const ThemeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
`;

const CustomQuestionAction = styled.div`
  display: flex;
  margin-top: 14px;
  padding-top: 8px;

  & > button {
    width: 100%;
  }
`;

const LibraryFilterToggle = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 4px;
`;

const LibraryFilterToggleButton = styled.button<{ $active: boolean }>`
  flex: 1 1 0;
  border: none;
  background: ${({ $active }) => ($active ? '#eff8ff' : '#ffffff')};
  color: ${({ $active }) => ($active ? '#1678c2' : '#475467')};
  padding: 8px 12px;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  text-align: center;
  cursor: pointer;

  & + & {
    border-left: 1px solid #d0d5dd;
  }
`;

const ThemeButton = styled.button<{ $active: boolean }>`
  text-align: left;
  border: 1px solid ${({ $active }) => ($active ? '#1678c2' : '#eaecf0')};
  background: ${({ $active }) => ($active ? '#eff8ff' : '#fcfcfd')};
  color: #1f2937;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  display: block;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};

  &:hover {
    border-color: ${({ $active }) => ($active ? '#1678c2' : '#d0d5dd')};
    background: ${({ $active }) => ($active ? '#eff8ff' : '#f9fafb')};
  }
`;

const ThemeButtonHeader = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const ThemeButtonLabel = styled.span`
  display: block;
  min-width: 0;
`;

const ThemeButtonCount = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.2;
  color: #78808d;
`;

const QuestionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  height: 100%;
`;

const CustomQuestionBuilder = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;

const BuilderHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const BuilderTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  color: #1f2937;
`;

const CustomQuestionRowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
`;

const CustomQuestionOrdinal = styled.div`
  font-size: 12px;
  color: #667085;
  font-weight: 600;
`;

const CustomQuestionFieldRow = styled.div`
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr);
  align-items: center;
  gap: 8px;

  & + & {
    margin-top: 10px;
  }

  @media (max-width: 700px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 6px;
  }
`;

const CustomQuestionFieldLabel = styled.div`
  font-family: 'Poppins';
  font-size: 13px;
  font-weight: 500;
  color: #333;
`;

const CustomQuestionFieldControl = styled.div`
  min-width: 0;

  & > * {
    width: 100%;
  }
`;

const QuestionTypeFieldControl = styled(CustomQuestionFieldControl)`
  max-width: 100%;
  position: relative;
  z-index: 2;

  .ui.dropdown .menu {
    z-index: 30;
    max-height: 220px;
    overflow-y: auto;
  }

  @media (max-width: 700px) {
    max-width: 100%;
  }
`;

const QuestionTypeControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;

  @media (max-width: 700px) {
    justify-content: flex-start;
  }
`;

const QuestionTypeDropdownWrap = styled.div`
  max-width: 240px;
  width: 100%;

  @media (max-width: 700px) {
    max-width: 100%;
  }
`;

const SubtleTrashButton = styled.button`
  border: none;
  background: transparent;
  color: #98a2b3;
  width: 28px;
  height: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  i.icon {
    margin: 0;
  }

  &:hover:not(:disabled) {
    color: #d92d20;
  }

  &:disabled {
    color: #d0d5dd;
    cursor: not-allowed;
  }
`;

const MultipleChoiceOptionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MultipleChoiceOptionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 28px;
  align-items: center;
  gap: 8px;
`;

const OptionActionRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 6px;
`;

const CustomQuestionList = styled(QuestionList)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: visible;
  padding-right: 6px;
`;

const QuestionRow = styled.div`
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  padding: 10px 12px;

  label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
  }

  input[type='checkbox'] {
    margin-top: 3px;
  }
`;

const CustomQuestionCard = styled(Card)`
  padding: 12px;
  border-radius: 8px;
  height: auto;
  overflow: visible;
`;

const QuestionText = styled.span`
  display: block;
  line-height: 1.35;
  color: #111827;
`;

const QuestionTextBlock = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const QuestionRowSubtitle = styled.span`
  font-size: 11px;
  line-height: 1.2;
  color: #98a2b3;
`;

const ModalActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const QuestionModalSelectedCount = styled.span`
  margin-right: auto;
  font-size: 14px;
  font-weight: 500;
  color: #373c47;
  margin-left: 20px;
`;
